import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Tag,
  Select,
  Dropdown,
  Button,
  Input,
  message,
} from "antd";

import { api } from "../../../../services/api";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";

function normalizeType(type) {
  return type[0].toUpperCase() + type.slice(1);
}

export default function ListView({
  onIssueClick,
  data,
  user,
  filters,
  types,
  onFilterChange,
}: any) {
  const issues = data?.list || [];
  const [searchText, setSearchText] = useState(filters.title || "");
  const updateTicket = async (id: string, body: any) => {
    try {
      await api.updateTicket(id, body);
      message.success("Ticket updated successfully!");
    } catch (error) {
      message.error("Failed to update the ticket.");
      console.error("Error updating ticket:", error);
    }
  };

  const [users, setUsers] = useState<
    { name: string; email: string; _id: string }[]
  >([]);

  useEffect(() => {
    if (data?.user) {
      setUsers(Object.values(data.user));
    }
  }, [data?.user]);

  const isMobile = window.innerWidth < 768;

  const statusItems = [
    { key: "open", label: "New" },
    { key: "progress", label: "In Progress" },
    { key: "hold", label: "Hold" },
    { key: "resolved", label: "Resolved" },
    { key: "rejected", label: "Rejected" },
    { key: "closed", label: "Closed" },
  ];
  const priorityColors = {
    P1: "red",
    P2: "orange",
    P3: "gold",
    P4: "green",
  };

  const normalizedIssues = issues.map((issue: any) => ({
    ...issue,
    type: normalizeType(issue.type),
    priority: `P${issue.priority}`,
  }));

  useEffect(() => {
    const handler = setTimeout(() => {
      onFilterChange("title", searchText);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchText]);

  const handleStatusMenuClick = (e) =>
    onFilterChange("status", e.key === "clear" ? null : e.key);

  const typeMenuProps: any = {
    items: types.map((t) => ({
      key: t.value, // use value as key
      label: t.label, // keep the label
    })),

    onClick: (e) => onFilterChange("type", e.key === "clear" ? null : e.key),
  };

  const statusMenuProps: any = {
    items: [
      ...statusItems,
      { type: "divider" },
      { key: "clear", label: "Clear Filter" },
    ],
    onClick: handleStatusMenuClick,
  };
  const priorityLevels = [1, 2, 3, 4];

  const priorityMenuProps = {
    items: priorityLevels.map((priority) => ({
      key: priority,
      label: (
        <div onClick={() => onFilterChange("priority", priority)}>
          P{priority}
        </div>
      ),
    })),
  };

  const columns: any = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
      render: (id: string) => <>#{id}</>,
    },
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8, // space between "Title" and input
            justifyContent: "flex-start", // align to the left
          }}
        >
          <span>Title</span>

          <Input
            prefix={<SearchOutlined />}
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: isMobile ? 120 : 200 }}
            size={isMobile ? "small" : "middle"}
          />
        </div>
      ),
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      width: isMobile ? 220 : 320, // Adjust width if needed
    },
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          Type
          <Dropdown menu={typeMenuProps} trigger={["click"]}>
            <Button
              type="text"
              size="small"
              icon={<FilterOutlined style={{ fontSize: "12px" }} />}
            />
          </Dropdown>
          {filters.type && (
            <Tag closable onClose={() => onFilterChange("type", null)}>
              {normalizeType(filters.type)}
            </Tag>
          )}
        </div>
      ),
      dataIndex: "type",
      key: "type",
    },

    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          Status
          <Dropdown menu={statusMenuProps} trigger={["click"]}>
            <Button
              type="text"
              size="small"
              icon={<FilterOutlined style={{ fontSize: "12px" }} />}
            />
          </Dropdown>
          {filters.status && (
            <Tag
              color="blue"
              closable
              onClose={() => onFilterChange("status", null)}
            >
              {statusItems.find((i) => i.key === filters.status)?.label ||
                filters.status}
            </Tag>
          )}
        </div>
      ),
      dataIndex: "status",
      key: "status",
      width: isMobile ? 100 : 150,
      render: (status, record) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Select
            defaultValue={status}
            // value={status}
            size="small"
            style={{ width: "85%" }}
            onChange={(newStatus) => {
              updateTicket(record.id, { status: newStatus });
            }}
            options={statusItems.map((s) => ({
              value: s.key,
              label: s.label,
            }))}
          />
        </div>
      ),
    },

    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          Priority
          <Dropdown menu={priorityMenuProps} trigger={["click"]}>
            <Button
              type="text"
              size="small"
              icon={<FilterOutlined style={{ fontSize: "12px" }} />}
            />
          </Dropdown>
          {filters.priority && (
            <Tag closable onClose={() => onFilterChange("priority", null)}>
              P{filters.priority}
            </Tag>
          )}
        </div>
      ),
      dataIndex: "priority",
      key: "priority",
      width: 100,
      render: (priority) => (
        <Tag color={priorityColors[priority] || "default"}>{priority}</Tag>
      ),
    },
    {
      title: "Assignee",
      dataIndex: "assignee",
      key: "assignee",
      responsive: ["md"],
      render: (assignee, record) => {
        const selectedUser = users.find(
          (u) => u._id === record?.assignee_id
        )?.name;

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <Select
              showSearch
              placeholder="Search or select assignee"
              filterOption={false}
              defaultValue={record?.assignee_id}
              onSearch={async (search) => {
                const response = await api.getAllUsers({ search });
                setUsers(response.data);
              }}
              onChange={(newStatus) => {
                updateTicket(record.id, { assignee_id: newStatus });
              }}
            >
              {users.map((user: any) => {
                return (
                  <Select.Option key={user._id} value={user._id}>
                    {user.name}
                  </Select.Option>
                );
              })}
            </Select>
          </div>
        );
      },
    },

    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          Requester
        </div>
      ),
      dataIndex: "user_id",
      key: "user_id",
      render: (userId: string) => {
        const userName = user?.[userId]?.name || "—";
        return userName;
      },
    },

    {
      title: "Last Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 180,
      render: (updated_at: any) => {
        if (!updated_at) return "-";

        const date = new Date(updated_at);

        const formattedDate = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });

        const formattedTime = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });

        return (
          <div style={{ wordWrap: "break-word" }}>
            <span>{formattedDate}</span>
            <br />
            <span>{formattedTime}</span>
          </div>
        );
      },
    },

    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (created_at: any) => {
        if (!created_at) return "-";

        const date = new Date(created_at);

        const formattedDate = date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });

        const formattedTime = date.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });

        return (
          <div style={{ wordWrap: "break-word" }}>
            <span>{formattedDate}</span>
            <br />
            <span>{formattedTime}</span>
          </div>
        );
      },
    },

    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          Aging
        </div>
      ),
      key: "aging",
      dataIndex: "created_at",
      render: (createdAt: string) => {
        if (!createdAt) return "—";

        const createdDate = new Date(createdAt);

        const now = new Date();

        const diffMs = now.getTime() - createdDate.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
        const diffMinutes = Math.floor((diffMs / (1000 * 60)) % 60);

        if (diffDays > 0)
          return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

        if (Math.floor(diffMs / (1000 * 60 * 60)) > 0) {
          let result = `${Math.floor(diffMs / (1000 * 60 * 60))} hour${
            Math.floor(diffMs / (1000 * 60 * 60)) > 1 ? "s" : ""
          }`;
          if (diffMinutes > 0) {
            result += ` ${diffMinutes} minute${diffMinutes > 1 ? "s" : ""}`;
          }
          return `${result} ago`;
        }

        if (Math.floor(diffMs / (1000 * 60)) > 0) {
          const diffMins = Math.floor(diffMs / (1000 * 60));
          return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
        }

        return "Just now";
      },
    },
  ];

  return (
    <div style={{ maxWidth: "100%", overflowX: "hidden" }}>
      <Card
        style={{ marginTop: 12 }}
        bodyStyle={{
          padding: isMobile ? "8px" : "24px",
          height: isMobile ? "59vh" : "70vh",
        }}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={normalizedIssues}
          locale={{ emptyText: "No Tickets..." }}
          pagination={{
            pageSize: isMobile ? 5 : 8,
            size: isMobile ? "small" : "default",
          }}
          scroll={{ x: "max-content" }}
          size={isMobile ? "small" : "middle"}
          onRow={(record) => ({
            onClick: (e) => {
              const tagName = (e.target as HTMLElement).tagName.toLowerCase();
              if (
                tagName === "svg" ||
                tagName === "path" ||
                tagName === "input" ||
                (e.target as HTMLElement).closest(".ant-select")
              ) {
                return;
              }
              onIssueClick(record);
            },
          })}
        />
      </Card>
    </div>
  );
}
